/**
 * Credit DNA Score™ Gauge — semi-circle SVG with rainbow gradient and dial.
 * Range: 300-850. Bands: 300-579 Poor (red), 580-669 Fair (orange),
 * 670-739 Good (yellow), 740-799 V.Good (lime), 800-850 Excellent (green).
 */
export default function CreditGauge({ score = 600, grade = "Fair", size = 200, label = "Credit DNA Score™" }) {
    const min = 300;
    const max = 850;
    const pct = Math.max(0, Math.min(1, (score - min) / (max - min)));
    const angle = -180 + pct * 180; // -180 (left) → 0 (right)

    const r = size / 2 - 14;
    const cx = size / 2;
    const cy = size / 2 + 4;

    const polar = (deg) => {
        const rad = (Math.PI / 180) * deg;
        return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
    };

    const [tx, ty] = polar(angle);

    return (
        <div className="flex flex-col items-center" data-testid="credit-gauge">
            <svg width={size} height={size / 2 + 28} viewBox={`0 0 ${size} ${size / 2 + 28}`}>
                <defs>
                    <linearGradient id="gauge-grad" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#FF0055" />
                        <stop offset="25%" stopColor="#FF6B00" />
                        <stop offset="50%" stopColor="#FFD600" />
                        <stop offset="75%" stopColor="#A8E300" />
                        <stop offset="100%" stopColor="#00D084" />
                    </linearGradient>
                </defs>
                {/* Track */}
                <path
                    d={describeArc(cx, cy, r, -180, 0)}
                    stroke="#E2E8F0"
                    strokeWidth="14"
                    strokeLinecap="round"
                    fill="none"
                />
                {/* Filled arc */}
                <path
                    d={describeArc(cx, cy, r, -180, angle)}
                    stroke="url(#gauge-grad)"
                    strokeWidth="14"
                    strokeLinecap="round"
                    fill="none"
                />
                {/* Dial */}
                <circle cx={tx} cy={ty} r="9" fill="#0A1930" stroke="white" strokeWidth="3" />
            </svg>
            <div className="-mt-10 text-center">
                <div className="font-display text-3xl font-bold text-[#0A1930]" data-testid="gauge-score">
                    {score}
                </div>
                <div className="text-xs font-semibold text-[#00D084] mt-1" data-testid="gauge-grade">
                    {grade}
                </div>
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-slate-500 mt-1 font-semibold">
                {label}
            </div>
        </div>
    );
}

function polarToCartesian(cx, cy, r, angleDeg) {
    const rad = ((angleDeg - 0) * Math.PI) / 180.0;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function describeArc(cx, cy, r, startAngle, endAngle) {
    const start = polarToCartesian(cx, cy, r, endAngle);
    const end = polarToCartesian(cx, cy, r, startAngle);
    const largeArc = endAngle - startAngle <= 180 ? "0" : "1";
    return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 0 ${end.x} ${end.y}`;
}
